import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@apunte-digital/powersync";
import { writeVerified } from "../powersync/write-verify";

/**
 * ── Why ALL the mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this form:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * In other words, in guest mode the UPDATE went out **without `WHERE`** → it
 * rewrote ALL `user` rows in the local database. `user` is NOT `localOnly`, so
 * every touched row also queued its own upload op (`ps_crud`).
 *
 * The premise behind it ("the guest database has only one `user` row") is false
 * during a guest→real upgrade: there the guest database CONNECTS with the real
 * account's connector, which subscribes itself to `organizations` and downloads
 * one `user` row for EACH co-member.
 *
 * The correct filter is NOT guessed: the guest row is seeded by `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid` is exactly
 * `VISITOR_ID` (see `apps/web/src/lib/auth.ts`). Thus `eq(user.id, userId)`
 * points to the SAME row that the unfiltered branch intended to touch when the
 * table had one row — identical behavior in the normal case, bounded
 * otherwise. It is also the form already used by
 * `updateUserFontFamilyMutation` and `updateUserImageMutation`, neither of
 * which had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 */

/**
 * ── §4.11 — why the 6 mutations below go through `writeVerified` ──────
 *
 * `apunte-digital-app@4cc1a014` (initial conversion to §4.11) left `user`
 * OUTSIDE the protection with this reasoning: "the server creates the row
 * before login; the guest insert has no synchronization window". An independent
 * audit on 2026-08-13
 * (`infra/reports/user-exclusion-audit/2026-08-13-summary.md`, criterion
 * §4.1 of `infra/guides/POWERSYNC_VERIFIED_WRITE.md`) CONCLUDED that premise
 * is false — and not only for the guest:
 *
 * - `db` (the PowerSync accessor) becomes non-null as soon as it opens the
 *   LOCAL SQLite, not when initial sync finishes (`connect()` is async and
 *   happens after opening the DB; see
 *   `apps/web/src/providers/power-sync-context.tsx`).
 * - `userId()` becomes active as soon as `signUpActor`/`signInActor` receive
 *   the server session — a REST call INDEPENDENT of the PowerSync stream.
 *   There is no `await hasSynced` before mounting `ThemeProvider`.
 * - (UNTIL 2026-08-13) The `ThemeProvider` `createEffect` that seeds the row
 *   (`db.select().from(user)...; if (existing.length === 0)
 *   db.insert(user)`) did NOT distinguish a guest from a real account: it only
 *   checked `if (!db || !uid) return`. It ran for ANY `uid`, so for a NEWLY
 *   REGISTERED account —not only the guest— the local insert could fire before
 *   the checkpoint brought down the row the server had already created.
 *
 * Reproduced in red with a double before this fix
 * (`__tests__/mutations-ghost-write.test.ts`): an `UPDATE ... WHERE id = ?`
 * against the PowerSync view can match ZERO rows and return "success" while
 * that row travels to the server — the user's write (language, theme, font,
 * avatar, blur, follow-device) is lost IN SILENCE. It is closed just like the
 * rest of §4.11: write → reread → compare → retry with backoff → throw if it
 * never sticks.
 *
 * ── Root-cause fix (2026-08-13) ─────────────────────────────────────────
 * `ThemeProvider` now distinguishes a guest from a real account: the guard is
 * `uid !== VISITOR_ID` (`apps/web/src/providers/theme-provider.tsx`), so the
 * local insert for a real account —the root cause measured above— no longer
 * happens. Measured without visual regression in
 * `__tests__/theme-provider-seed-window.test.ts` (missing row vs. default-seeded
 * row resolve the same theme) and protected against code regression in
 * `apps/web/src/providers/__tests__/theme-provider-seed-guard.test.tsx`
 * (red without the guard, green with it).
 *
 * ── Why `writeVerified` STILL belongs here (defense in depth, NOT a remnant
 *    of the already-closed defect) ─────────────────────────────────────
 * Closing `ThemeProvider`'s seed closes ONE source of the window, not the
 * window itself: `UPDATE ... WHERE id = ?` can still match ZERO rows against
 * the PowerSync view just after the SERVER creates the row (signup/OAuth) and
 * BEFORE the first checkpoint brings it down — no local insert is needed for
 * that, only for the user to change a preference quickly after
 * registering. The
 * guest→real upgrade (comment above) still downloads one `user` row per
 * co-member through the `organizations` subscription, with its own checkpoint
 * window. `writeVerified` does not defend against the already-closed bug —
 * it defends against the general pattern of an `UPDATE` that "succeeds" without
 * having touched anything. Do NOT remove it merely because `ThemeProvider`'s
 * seed no longer competes.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
): Promise<void> {
  await writeVerified({
    write: async () => {
      await db.update(user).set({ locale }).where(eq(user.id, userId));
    },
    reread: () => db.select().from(user).where(eq(user.id, userId)).limit(1),
    verify: (rows) => rows[0]?.locale === locale,
    describe: `user.locale de ${userId}`,
  });
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
): Promise<void> {
  await writeVerified({
    write: async () => {
      await db.update(user).set({ font_family: fontFamilyId }).where(eq(user.id, userId));
    },
    reread: () => db.select().from(user).where(eq(user.id, userId)).limit(1),
    verify: (rows) => rows[0]?.font_family === fontFamilyId,
    describe: `user.font_family de ${userId}`,
  });
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through
 * REST: the new photo is visible immediately and survives a cold reload without
 * a connection. The BINARY goes through REST (`POST /api/avatar`) because it
 * does not fit in the sync queue; only the returned key travels here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * The guest has no synchronizable row of its own and cannot have an avatar
 * (rule zero), so there is no `VISITOR_ID` branch here as for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
): Promise<void> {
  await writeVerified({
    write: async () => {
      await db.update(user).set({ image: imageKey }).where(eq(user.id, userId));
    },
    reread: () => db.select().from(user).where(eq(user.id, userId)).limit(1),
    verify: (rows) => rows[0]?.image === imageKey,
    describe: `user.image de ${userId}`,
  });
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
): Promise<void> {
  await writeVerified({
    write: async () => {
      await db.update(user).set({ global_theme_id: themeId }).where(eq(user.id, userId));
    },
    reread: () => db.select().from(user).where(eq(user.id, userId)).limit(1),
    verify: (rows) => rows[0]?.global_theme_id === themeId,
    describe: `user.global_theme_id de ${userId}`,
  });
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
): Promise<void> {
  const value = isBlur ? 1 : 0;
  await writeVerified({
    write: async () => {
      await db.update(user).set({ is_blur: value }).where(eq(user.id, userId));
    },
    reread: () => db.select().from(user).where(eq(user.id, userId)).limit(1),
    verify: (rows) => rows[0]?.is_blur === value,
    describe: `user.is_blur de ${userId}`,
  });
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
): Promise<void> {
  const value = followDevice ? 1 : 0;
  await writeVerified({
    write: async () => {
      await db.update(user).set({ follow_device_theme: value }).where(eq(user.id, userId));
    },
    reread: () => db.select().from(user).where(eq(user.id, userId)).limit(1),
    verify: (rows) => rows[0]?.follow_device_theme === value,
    describe: `user.follow_device_theme de ${userId}`,
  });
}
